import { useEffect, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { RequireOnboarded } from '../../components/app/RequireOnboarded';
import { useI18n } from '../../i18n';
import { useApiAuth } from '../../lib/useApi';
import { LeaderRow } from '../../components/boards/LeaderRow';
import { ChatPanel } from '../../components/match/ChatPanel';
import { leagueChatSource } from '../../lib/comments';
import {
  deleteLeague,
  getLeague,
  getLeagueBoard,
  leaveLeague,
  type League,
  type LeaderboardEntry,
} from '../../lib/boards';

export function LeagueDetail({ id }: { id: string }) {
  return (
    <RequireOnboarded>
      <AppLayout>
        <LeagueInner id={id} />
      </AppLayout>
    </RequireOnboarded>
  );
}

function LeagueInner({ id }: { id: string }) {
  const { t, localePath } = useI18n();
  const auth = useApiAuth();
  const [league, setLeague] = useState<League | null>(null);
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([getLeague(auth, id), getLeagueBoard(auth, id)])
      .then(([lg, board]) => {
        if (!alive) return;
        setLeague(lg);
        setEntries(board.entries);
      })
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  async function onLeave() {
    if (busy) return;
    setBusy(true);
    try {
      await leaveLeague(auth, id);
      location.replace(localePath('/boards'));
    } catch {
      setBusy(false);
    }
  }
  async function onDelete() {
    if (busy || !confirm(t('boards.delete_confirm'))) return;
    setBusy(true);
    try {
      await deleteLeague(auth, id);
      location.replace(localePath('/boards'));
    } catch {
      setBusy(false);
    }
  }

  if (loading) return <div class="bc-spinner">{t('app.loading')}</div>;
  if (notFound || !league) {
    return (
      <div class="bc-empty">
        {t('boards.league_notfound')}
        <div style={{ marginTop: 16 }}>
          <a class="bc-pill" href={localePath('/boards')}>{t('boards.back')}</a>
        </div>
      </div>
    );
  }

  const isOwner = league.role === 'owner';

  return (
    <div class="ld">
      <a class="md__back" href={localePath('/boards')}>← {t('boards.back')}</a>

      <div class="ld__head">
        <span class="ld__emoji">{league.emoji ?? '🏆'}</span>
        <div>
          <h1 class="ld__name bc-serif">{league.name}</h1>
          <p class="ld__meta">
            {league.members} {t('boards.members')} · {t('boards.code')}: <code>{league.id}</code>
          </p>
        </div>
      </div>

      {league.banner && <div class="bc-card ld__banner">{league.banner}</div>}

      <div class="lr-list">
        {entries.map((e) => (
          <LeaderRow key={e.userId} e={e} />
        ))}
        {entries.length === 0 && <p class="bc-empty">{t('boards.empty')}</p>}
      </div>

      <div class="bc-card md__chat">
        <div class="bc-label">{t('boards.chat')}</div>
        <ChatPanel source={leagueChatSource(auth, id)} />
      </div>

      <div class="ld__actions">
        {isOwner ? (
          <button class="bc-pill ld__danger" disabled={busy} onClick={onDelete}>
            {t('boards.delete')}
          </button>
        ) : (
          <button class="bc-pill" disabled={busy} onClick={onLeave}>
            {t('boards.leave')}
          </button>
        )}
      </div>
    </div>
  );
}
